'use client'

import { useCallback, useDeferredValue, useMemo, useRef, useState } from 'react'
import type { LatLngLiteral, MapLocatorItemType, MapLocatorWidgetType } from 'ecosystem'
import {
  DEFAULT_CLUSTERING_SETTINGS,
  DEFAULT_LABELS,
  DEFAULT_MAP_SETTINGS,
  DEFAULT_MARKER_SETTINGS,
  DEFAULT_ORIGIN_MARKER_SETTINGS,
  DEFAULT_POPOVER_SETTINGS,
  DEFAULT_SEARCH_SETTINGS
} from './constants'
import { distanceInKm, isPlottable, itemPosition, itemSearchIndex, normalizeText } from './utils'

export interface LocatorOrigin extends LatLngLiteral {
  label?: string
}

export type MapLocatorEntry = MapLocatorItemType & {
  /** Distance from the current origin in km, undefined while no origin is set */
  distanceKm?: number
}

/**
 * Merges stored settings over the defaults so a partially configured widget
 * — or one authored by hand — still renders.
 */
export const useMapLocatorSettings = (widget: MapLocatorWidgetType) =>
  useMemo(
    () => ({
      map: { ...DEFAULT_MAP_SETTINGS, ...widget.map },
      clustering: { ...DEFAULT_CLUSTERING_SETTINGS, ...widget.clustering },
      markers: { ...DEFAULT_MARKER_SETTINGS, ...widget.markers },
      originMarker: { ...DEFAULT_ORIGIN_MARKER_SETTINGS, ...widget.originMarker },
      popover: { ...DEFAULT_POPOVER_SETTINGS, ...widget.popover },
      search: { ...DEFAULT_SEARCH_SETTINGS, ...widget.search },
      labels: { ...DEFAULT_LABELS, ...widget.labels },
      tags: widget.tags ?? [],
      layout: widget.layout ?? 'MAP_WITH_LIST'
    }),
    [widget]
  )

export type MapLocatorSettings = ReturnType<typeof useMapLocatorSettings>

interface UseMapLocatorParams {
  items: MapLocatorItemType[]
  settings: MapLocatorSettings
}

/**
 * A camera request, separate from the selection itself: clicking a marker only
 * recentres, while picking from the list must also zoom in far enough that the
 * point is not swallowed by its cluster. `nonce` lets the same item be focused
 * twice in a row.
 */
export interface FocusRequest {
  id: string
  zoomIn: boolean
  nonce: number
}

export const useMapLocator = ({ items, settings }: UseMapLocatorParams) => {
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [query, setQuery] = useState('')
  const [origin, setOrigin] = useState<LocatorOrigin | null>(null)
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  const [focus, setFocus] = useState<FocusRequest | null>(null)
  const focusCount = useRef(0)

  const plottableItems = useMemo(() => items.filter(isPlottable), [items])

  /**
   * Folding case and diacritics is the expensive part of matching, and the item
   * data never changes while the visitor types — so it is done once per data set
   * rather than once per item per keystroke.
   */
  const searchIndex = useMemo(() => {
    const index = new Map<string, string>()

    plottableItems.forEach((item) => index.set(item.id, itemSearchIndex(item)))

    return index
  }, [plottableItems])

  /**
   * Filtering 294 points and re-rendering the marker set is heavy enough to drop
   * keystrokes. Deferring it keeps the input on the urgent path and lets React
   * interrupt an in-progress filter when the next character arrives.
   */
  const deferredQuery = useDeferredValue(query)

  const entries = useMemo<MapLocatorEntry[]>(() => {
    const normalizedQuery = normalizeText(deferredQuery.trim())
    const { radiusKm } = settings.search

    const filtered = plottableItems.filter((item) => {
      if (selectedTagIds.length && !selectedTagIds.some((id) => item.tagIds?.includes(id))) {
        return false
      }

      if (normalizedQuery && !(searchIndex.get(item.id) ?? '').includes(normalizedQuery)) {
        return false
      }

      return true
    })

    if (!origin) {
      return filtered
    }

    const withDistance = filtered.map((item) => ({
      ...item,
      distanceKm: distanceInKm(origin, itemPosition(item))
    }))

    return (radiusKm ? withDistance.filter((item) => item.distanceKm <= radiusKm) : withDistance)
      .slice()
      .sort((a, b) => a.distanceKm - b.distanceKm)
  }, [plottableItems, searchIndex, deferredQuery, selectedTagIds, origin, settings.search])

  const activeItem = useMemo(
    () => entries.find((item) => item.id === activeItemId) ?? null,
    [entries, activeItemId]
  )

  const selectItem = useCallback((item: MapLocatorEntry | null, options?: { zoomIn?: boolean }) => {
    setActiveItemId(item?.id ?? null)

    if (!item) {
      setFocus(null)

      return
    }

    focusCount.current += 1
    setFocus({ id: item.id, zoomIn: Boolean(options?.zoomIn), nonce: focusCount.current })
  }, [])

  const toggleTag = useCallback((tagId: string) => {
    setActiveItemId(null)
    setFocus(null)
    setSelectedTagIds((current) =>
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]
    )
  }, [])

  const clearFilters = useCallback(() => {
    setSelectedTagIds([])
    setQuery('')
    setOrigin(null)
    setActiveItemId(null)
    setFocus(null)
  }, [])

  const hasActiveFilters = Boolean(selectedTagIds.length || query.trim() || origin)
  /** True while the deferred filter is still catching up with the input. */
  const isFiltering = query !== deferredQuery

  return {
    entries,
    totalCount: plottableItems.length,
    selectedTagIds,
    toggleTag,
    query,
    setQuery,
    origin,
    setOrigin,
    activeItem,
    activeItemId,
    selectItem,
    focus,
    clearFilters,
    hasActiveFilters,
    isFiltering
  }
}

export type MapLocatorState = ReturnType<typeof useMapLocator>
